import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, Users, MessageCircle, Phone, Mail, Calendar, Clock, Target, Award, CheckCircle, Activity, Zap } from 'lucide-react';
import { XAxis, YAxis, CartesianGrid, Tooltip, Legend, LineChart, Line, PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { analyzeCommunicationPerformance } from '../../../services/openai';

const ConversationAnalytics = ({ metrics, leads = [] }) => {
  const [timeRange, setTimeRange] = useState('30d');
  const [selectedChannel, setSelectedChannel] = useState('all');
  const [aiInsights, setAiInsights] = useState(null);
  const [loading, setLoading] = useState(false);

  // Mock conversation data - in real implementation, this would come from Supabase
  const conversationData = {
    daily: [
      { date: '2025-01-01', calls: 15, emails: 45, sms: 23, responses: 28, closes: 4 },
      { date: '2025-01-02', calls: 18, emails: 52, sms: 31, responses: 32, closes: 6 },
      { date: '2025-01-03', calls: 12, emails: 38, sms: 19, responses: 21, closes: 3 },
      { date: '2025-01-04', calls: 22, emails: 61, sms: 28, responses: 38, closes: 8 },
      { date: '2025-01-05', calls: 25, emails: 58, sms: 35, responses: 41, closes: 9 },
      { date: '2025-01-06', calls: 19, emails: 47, sms: 26, responses: 31, closes: 5 },
      { date: '2025-01-07', calls: 21, emails: 54, sms: 29, responses: 35, closes: 7 }
    ],
    channels: [
      { name: 'Email', value: 352, color: '#3B82F6' },
      { name: 'Phone', value: 132, color: '#10B981' },
      { name: 'SMS', value: 191, color: '#F59E0B' },
      { name: 'Social', value: 89, color: '#8B5CF6' }
    ],
    performance: [
      { channel: 'Email', sent: 352, opened: 187, clicked: 94, responded: 67, closed: 23, rate: 6.5 },
      { channel: 'Phone', attempted: 132, connected: 89, qualified: 56, responded: 45, closed: 18, rate: 13.6 },
      { channel: 'SMS', sent: 191, delivered: 186, opened: 142, responded: 38, closed: 12, rate: 6.3 },
      { channel: 'Social', sent: 89, viewed: 67, engaged: 34, responded: 21, closed: 8, rate: 9.0 }
    ],
    sentiment: [
      { sentiment: 'Positive', count: 156, percentage: 62 },
      { sentiment: 'Neutral', count: 67, percentage: 27 },
      { sentiment: 'Negative', count: 27, percentage: 11 }
    ]
  };

  useEffect(() => {
    generateAIInsights();
  }, [timeRange, selectedChannel]);

  const generateAIInsights = async () => {
    setLoading(true);
    try {
      const communicationData = {
        totalOutreach: conversationData?.channels?.reduce((sum, ch) => sum + ch?.value, 0),
        responseRate: 21.4,
        closeRate: 8.2,
        channels: conversationData?.performance,
        sentiment: conversationData?.sentiment,
        topPerformers: leads?.filter(lead => lead?.score > 80),
        timeRange: timeRange
      };

      const insights = await analyzeCommunicationPerformance(communicationData);
      setAiInsights(insights);
    } catch (error) {
      console.error('Error generating AI insights:', error);
      // Fallback insights
      setAiInsights({
        summary: 'Communication analysis shows strong email performance with room for improvement in SMS engagement.',
        key_findings: [
          'Email has the highest volume but lower conversion rates',
          'Phone calls show best close rates despite lower volume',
          'SMS engagement is declining over time'
        ],
        channel_recommendations: [
          {
            channel: 'Phone',
            recommendation: 'Increase call volume for hot leads (score >80)',
            expected_impact: '15-20% improvement in close rate'
          },
          {
            channel: 'Email',
            recommendation: 'Improve subject lines and personalization',
            expected_impact: '5-8% increase in open rates'
          }
        ],
        optimization_opportunities: [
          {
            area: 'Timing Optimization',
            current_performance: 'Average response time: 4.2 hours',
            improvement_potential: 'Target <2 hour response time',
            action_items: ['Set up auto-responses', 'Create response schedules', 'Implement urgency triggers']
          }
        ],
        next_steps: [
          { step: 'Implement phone-first approach for hot leads', priority: 'high', timeline: 'This week' },
          { step: 'A/B test email templates', priority: 'medium', timeline: '2 weeks' }
        ]
      });
    } finally {
      setLoading(false);
    }
  };

  const getChannelColor = (channel) => {
    const colors = {
      'Email': 'bg-blue-100 text-blue-800',
      'Phone': 'bg-green-100 text-green-800',
      'SMS': 'bg-yellow-100 text-yellow-800',
      'Social': 'bg-purple-100 text-purple-800'
    };
    return colors?.[channel] || 'bg-gray-100 text-gray-800';
  };

  const getChannelIcon = (channel) => {
    const icons = {
      'Email': Mail,
      'Phone': Phone,
      'SMS': MessageCircle,
      'Social': Users
    };
    return icons?.[channel] || MessageCircle;
  };

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div className="flex gap-2">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e?.target?.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
          </select>
          
          <select
            value={selectedChannel}
            onChange={(e) => setSelectedChannel(e?.target?.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Channels</option>
            <option value="email">Email</option>
            <option value="phone">Phone</option>
            <option value="sms">SMS</option>
            <option value="social">Social</option>
          </select>
        </div>
        
        <button
          onClick={generateAIInsights}
          disabled={loading}
          className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-colors disabled:opacity-50 flex items-center"
        >
          {loading ? (
            <>
              <motion.div 
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                className="h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"
              />
              Analyzing...
            </>
          ) : (
            <>
              <Zap className="h-4 w-4 mr-2" />
              Generate AI Insights
            </>
          )}
        </button>
      </div>
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Conversations</p>
              <p className="text-3xl font-bold text-gray-900">764</p>
              <div className="flex items-center mt-2 text-sm text-green-600">
                <TrendingUp className="h-4 w-4 mr-1" />
                +12.5% vs last period
              </div>
            </div>
            <MessageCircle className="h-8 w-8 text-blue-600" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Response Rate</p>
              <p className="text-3xl font-bold text-gray-900">21.4%</p>
              <div className="flex items-center mt-2 text-sm text-green-600">
                <TrendingUp className="h-4 w-4 mr-1" />
                +3.2% vs last period
              </div>
            </div>
            <Activity className="h-8 w-8 text-green-600" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Close Rate</p>
              <p className="text-3xl font-bold text-gray-900">8.2%</p>
              <div className="flex items-center mt-2 text-sm text-red-600">
                <TrendingDown className="h-4 w-4 mr-1" />
                -1.1% vs last period
              </div>
            </div>
            <Target className="h-8 w-8 text-yellow-600" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Avg Response Time</p>
              <p className="text-3xl font-bold text-gray-900">4.2h</p>
              <div className="flex items-center mt-2 text-sm text-yellow-600">
                <Clock className="h-4 w-4 mr-1" />
                Target: &lt;2h
              </div>
            </div>
            <Clock className="h-8 w-8 text-purple-600" />
          </div>
        </motion.div>
      </div>
      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Activity Chart */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Daily Communication Activity</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={conversationData?.daily}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tickFormatter={(value) => new Date(value)?.getMonth() + 1 + '/' + new Date(value)?.getDate()} />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="calls" stroke="#10B981" strokeWidth={2} name="Calls" />
              <Line type="monotone" dataKey="emails" stroke="#3B82F6" strokeWidth={2} name="Emails" />
              <Line type="monotone" dataKey="sms" stroke="#F59E0B" strokeWidth={2} name="SMS" />
              <Line type="monotone" dataKey="closes" stroke="#EF4444" strokeWidth={3} name="Closes" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Channel Distribution */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Channel Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={conversationData?.channels}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100)?.toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {conversationData?.channels?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry?.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Channel Performance Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Channel Performance Analysis</h3>
          <p className="text-gray-600 mt-1">Detailed breakdown of each communication channel's effectiveness</p>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Channel
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Sent
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Engagement
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Responses
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Closes
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Close Rate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ROI
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {conversationData?.performance?.map((channel) => {
                const ChannelIcon = getChannelIcon(channel?.channel);
                const totalSent = channel?.sent || channel?.attempted;
                const engagement = channel?.opened || channel?.connected || channel?.delivered || channel?.viewed;
                const engagementRate = totalSent ? ((engagement / totalSent) * 100)?.toFixed(1) : '0';
                
                return (
                  <tr key={channel?.channel} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <ChannelIcon className="h-5 w-5 mr-3 text-gray-400" />
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getChannelColor(channel?.channel)}`}>
                          {channel?.channel}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {totalSent}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {engagement} ({engagementRate}%)
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {channel?.responded}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {channel?.closed}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className={`text-sm font-medium ${
                          channel?.rate >= 10 ? 'text-green-600' :
                          channel?.rate >= 5 ? 'text-yellow-600' : 'text-red-600'
                        }`}>
                          {channel?.rate}%
                        </span>
                        {channel?.rate >= 10 ? (
                          <TrendingUp className="h-4 w-4 ml-1 text-green-500" />
                        ) : (
                          <TrendingDown className="h-4 w-4 ml-1 text-red-500" />
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {channel?.rate >= 10 ? 'High' : channel?.rate >= 5 ? 'Medium' : 'Low'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {/* AI Insights */}
      {aiInsights && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200 p-6"
        >
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <Zap className="h-5 w-5 mr-2 text-purple-600" />
                AI Communication Insights
              </h3>
              <p className="text-gray-600 mt-1">AI-powered analysis of your communication performance</p>
            </div>
            <Award className="h-8 w-8 text-purple-600" />
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
                Key Findings
              </h4>
              <ul className="space-y-2">
                {aiInsights?.key_findings?.map((finding, index) => (
                  <li key={index} className="flex items-start text-sm text-gray-700">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    {finding}
                  </li>
                ))}
              </ul>
            </div>
            
            <div>
              <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                <Target className="h-4 w-4 mr-2 text-blue-500" />
                Optimization Opportunities
              </h4>
              <div className="space-y-3">
                {aiInsights?.optimization_opportunities?.map((opportunity, index) => (
                  <div key={index} className="bg-white rounded-lg p-3 border">
                    <h6 className="font-medium text-gray-800 mb-1">{opportunity?.area}</h6>
                    <p className="text-sm text-gray-600 mb-2">
                      Current: {opportunity?.current_performance} → Target: {opportunity?.improvement_potential}
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {opportunity?.action_items?.slice(0, 2)?.map((item, actionIndex) => (
                        <span key={actionIndex} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-blue-200">
            <h4 className="font-medium text-gray-900 mb-3 flex items-center">
              <Calendar className="h-4 w-4 mr-2 text-orange-500" />
              Recommended Next Steps
            </h4>
            <div className="space-y-2">
              {aiInsights?.next_steps?.map((step, index) => (
                <div key={index} className="flex items-center p-2 bg-white rounded border">
                  <div className={`w-3 h-3 rounded-full mr-3 ${
                    step?.priority === 'high' ? 'bg-red-500' :
                    step?.priority === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                  }`}></div>
                  <div className="flex-1">
                    <span className="text-sm font-medium text-gray-900">{step?.step}</span>
                    <span className="text-xs text-gray-500 ml-2">({step?.timeline})</span>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs ${
                    step?.priority === 'high' ? 'bg-red-100 text-red-800' :
                    step?.priority === 'medium'? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                  }`}>
                    {step?.priority}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default ConversationAnalytics;